<script setup lang="ts">
import type { AwardTier } from './milestone-award-scene';
import { Tooltip } from '@youcan/celeste';
import Skelly from '@/components/skelly/skelly.vue';
import { useDir } from '@/composables/use-dir';
import { useStatsStore } from '@/stores/stats';
import MilestoneAward from './milestone-award.vue';

const { t } = useI18n();
const page = usePage();
const formatter = useMoneyFormatter();
const statsStore = useStatsStore();
const { isRtl } = useDir();

interface Milestone {
  key: AwardTier;
  label: string;
  threshold: number;
  threshold_label: string;
  icon: string;
  color: string;
}

const isAwardOpen = ref(false);
const awardTier = ref<Milestone | null>(null);

onMounted(() => {
  statsStore.fetchStats();
});

const gmv = computed(() => statsStore.stats ? Number(statsStore.stats.gmv) : 0);

const formattedGmv = computed(() => formatter.format(gmv.value));

const ownerName = computed(() => [page.props.user?.first_name, page.props.user?.last_name].filter(Boolean).join(' '));

const milestonesArray: Milestone[] = [
  { key: 'bronze', label: t('home.milestones.bronze'), threshold: 1_000, threshold_label: '$1K', icon: url('bronze'), color: '#B5834F' },
  { key: 'silver', label: t('home.milestones.silver'), threshold: 10_000, threshold_label: '$10K', icon: url('silver'), color: '#A9ADB1' },
  { key: 'gold', label: t('home.milestones.gold'), threshold: 100_000, threshold_label: '$100K', icon: url('gold'), color: '#E6B52E' },
  { key: 'platinum', label: t('home.milestones.platinum'), threshold: 1_000_000, threshold_label: '$1M', icon: url('platinum'), color: '#535A62' },
  { key: 'diamond', label: t('home.milestones.diamond'), threshold: 10_000_000, threshold_label: '$10M', icon: url('diamond'), color: '#9EABB8' },
];

const currentTier = computed(() => {
  for (let i = milestonesArray.length - 1; i >= 0; i--) {
    if (gmv.value >= milestonesArray[i].threshold) {
      return milestonesArray[i];
    }
  }
  return null;
});

const nextTier = computed(() => {
  if (!currentTier.value) {
    return milestonesArray[0];
  }
  const currentIndex = milestonesArray.findIndex(m => m.key === currentTier.value!.key);
  return currentIndex < milestonesArray.length - 1 ? milestonesArray[currentIndex + 1] : null;
});

const progress = computed(() => {
  if (!currentTier.value && nextTier.value) {
    return (gmv.value / nextTier.value.threshold) * 100;
  }

  if (currentTier.value && nextTier.value) {
    const current = gmv.value - currentTier.value.threshold;
    const total = nextTier.value.threshold - currentTier.value.threshold;

    return (current / total) * 100;
  }

  return 100;
});

function lightenColor(hex: string, percent: number): string {
  const num = Number.parseInt(hex.replace('#', ''), 16);

  const r = (num >> 16);
  const g = ((num >> 8) & 0x00FF);
  const b = (num & 0x0000FF);

  return `#${(
    Math.min(255, Math.floor(r + (255 - r) * (percent / 100))) << 16
    | Math.min(255, Math.floor(g + (255 - g) * (percent / 100))) << 8
    | Math.min(255, Math.floor(b + (255 - b) * (percent / 100)))
  ) .toString(16).padStart(6, '0')}`;
}

const barStyle = computed(() => {
  const color = currentTier.value?.color || nextTier.value!.color;
  const lighterColor = lightenColor(color, 17);
  const progressPercent = Math.min(progress.value, 100);

  return {
    background: `
      repeating-linear-gradient(
        -45deg,
        ${color},
        ${color} 4px,
        ${lighterColor} 4px,
        ${lighterColor} 8px
      ) ${isRtl.value ? 'right' : 'left'} center / ${progressPercent}% 100% no-repeat,
      var(--color-neutral-200)
    `,
  };
});

const cardGradient = computed(() => {
  const color = currentTier.value?.color || nextTier.value?.color;

  if (!currentTier.value) {
    return {
      background: `linear-gradient(56deg, rgb(255 255 255 / 0%) 17.37%, rgb(128 128 128 / 3%) 59.25%, rgb(96 96 96 / 8%) 90.5%), #fff`,
    };
  }

  if (!color) {
    return {
      background: `linear-gradient(56deg, rgb(255 255 255 / 0%) 17.37%, rgb(255 249 246 / 3%) 59.25%, rgb(214 133 84 / 8%) 90.5%), #fff`,
    };
  }

  const rgb = {
    r: Number.parseInt(color.slice(1, 3), 16),
    g: Number.parseInt(color.slice(3, 5), 16),
    b: Number.parseInt(color.slice(5, 7), 16),
  };

  return {
    background: `linear-gradient(56deg, rgb(255 255 255 / 0%) 17.37%, rgb(${rgb.r} ${rgb.g} ${rgb.b} / 3%) 59.25%, rgb(${rgb.r} ${rgb.g} ${rgb.b} / 8%) 90.5%), #fff`,
  };
});

function openAward(tier: Milestone) {
  awardTier.value = tier;
  isAwardOpen.value = true;
}

function url(milestone: string) {
  return new URL(`/resources/app/src/assets/svg/milestones/${milestone}.svg`, import.meta.url).href;
}
</script>

<template>
  <div class="milestones" :style="cardGradient">
    <div class="title">
      <span>{{ $t('home.milestones.title') }}</span>
      <Tooltip :description="$t('home.milestones.tooltip')">
        <i i-celeste-information-fill />
      </Tooltip>
    </div>

    <div v-if="statsStore.pending" class="inner skeleton-container">
      <Skelly class="skeleton-icon" />
      <div class="skeleton-bar-container">
        <Skelly />
        <Skelly />
        <Skelly />
      </div>
    </div>

    <div
      v-else
      class="inner"
      role="presentation"
    >
      <button
        v-if="currentTier"
        type="button"
        class="current-tier-icon award-trigger"
        :aria-label="$t('home.milestones.award.open', { name: $t(`home.milestones.award.${currentTier.key}.name`) })"
        @click="openAward(currentTier)"
      >
        <span class="trophy">
          <img
            draggable="false"
            :src="currentTier.icon"
            :alt="currentTier.label"
          >
        </span>
      </button>
      <div v-else-if="nextTier" class="current-tier-icon">
        <span class="trophy is-locked">
          <img
            draggable="false"
            :src="nextTier.icon"
            :alt="nextTier.label"
            class="not-reached"
          >
        </span>
        <div role="presentation" class="lock">
          <i i-celeste-lock-line />
        </div>
      </div>

      <div class="bar-container">
        <div class="threshold-labels">
          <span class="tier-start">{{ currentTier?.threshold_label || '$0' }}</span>
          <span v-if="nextTier" class="tier-end">{{ nextTier.threshold_label }}</span>
          <span v-else class="tier-end">Max</span>
        </div>

        <Tooltip :description="formattedGmv">
          <div class="bar" :style="barStyle" />
        </Tooltip>

        <div class="tier-labels">
          <span class="tier-label">
            <template v-if="currentTier">
              <img
                draggable="false"
                :src="currentTier?.icon"
                :alt="currentTier?.label"
              >
              <span>{{ currentTier?.label }}</span>
            </template>
          </span>

          <span class="tier-label">
            <template v-if="nextTier">
              <img
                draggable="false"
                :src="nextTier?.icon"
                :alt="nextTier?.label"
              >
              <span>{{ nextTier?.label }}</span>
            </template>
          </span>
        </div>
      </div>
    </div>

    <MilestoneAward
      v-if="awardTier"
      v-model:open="isAwardOpen"
      :tier="awardTier.key"
      :icon="awardTier.icon"
      :owner="ownerName"
      :name="$t(`home.milestones.award.${awardTier.key}.name`)"
      :reason="$t(`home.milestones.award.${awardTier.key}.reason`, { amount: awardTier.threshold_label })"
    />
  </div>
</template>

<style scoped>
.milestones * {
  user-select: none;
}

.milestones {
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  padding: 12px;
  border: 1px solid var(--stroke-soft-200, #e1e4ea);
  border-radius: 12px;
  box-shadow: 0 2px 2px 0 rgb(202 207 216 / 10%);
  gap: 24px;
}

.milestones .inner {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@screen lt-lg {
  .milestones {
    gap: 0;
  }

  .milestones .inner {
    flex-direction: row;
    align-items: center;
  }

  .milestones .inner .bar-container {
    flex-grow: 1;
  }

  .milestones .inner .current-tier-icon img {
    width: 80px;
    height: auto;
    user-select: none;
  }
}

.title {
  display: flex;
  align-items: center;
  color: var(--color-text-strong-950);
  font: var(--label-md);
  gap: var(--spacing-6);
}

.title i {
  width: 16px;
  height: 16px;
  margin-top: 1px;
  color: var(--color-icon-sub-600);
}

.current-tier-icon {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
}

.current-tier-icon .lock {
  position: absolute;
  top: 70%;
  left: 50%;
  padding: 4px;
  transform: translate(-50%, -50%);
  border: 1px solid var(--color-stroke-soft-200);
  border-radius: var(--radius-6);
  background-color: var(--color-bg-white-0);
}

.current-tier-icon .lock i {
  display: block;
  width: 16px;
  height: 16px;
  color: var(--color-icon-sub-600);
}

.current-tier-icon img {
  width: 120px;
  height: 120px;
}

.trophy {
  display: block;
  position: relative;
  isolation: isolate;
}

.trophy::before {
  content: '';
  position: absolute;
  z-index: -1;
  bottom: 2%;
  left: 50%;
  width: 42%;
  height: 6%;
  transform: translateX(-50%);
  transition:
    transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1),
    opacity 0.4s cubic-bezier(0.25, 0.8, 0.25, 1),
    filter 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 50%;
  background: radial-gradient(closest-side, rgb(14 18 27 / 16%), transparent);
}

.trophy.is-locked::before {
  opacity: 0.4;
}

.current-tier-icon img.not-reached {
  opacity: 0.4;
  filter: grayscale(100%);
}

.award-trigger {
  border-radius: var(--radius-12);
  cursor: pointer;
}

.award-trigger img {
  transform-origin: 50% 90%;
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.award-trigger:hover img,
.award-trigger:focus-visible img {
  transform: translateY(-2px) scale(1.04);
}

.award-trigger:hover .trophy::before,
.award-trigger:focus-visible .trophy::before {
  transform: translateX(-50%) scaleX(0.95);
  opacity: 0.85;
  filter: blur(1.5px);
}

.bar {
  position: relative;
  width: 100%;
  height: 10px;
  margin: var(--spacing-8) 0;
  overflow: hidden;
  border-radius: 999px;
  box-shadow:
    -1px 0 1px 0 rgb(255 255 255 / 40%) inset,
    0 1px 1px 0 rgb(255 255 255 / 40%) inset;
}

.threshold-labels {
  display: flex;
  justify-content: space-between;
  width: 100%;
  color: var(--color-text-sub-600);
  font: var(--label-xs);
  font-weight: 600;
}

.tier-labels {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.tier-labels .tier-label {
  display: flex;
  align-items: center;
  color: var(--color-text-sub-600);
  font: var(--label-xs);
  gap: var(--spacing-2);
}

.tier-labels .tier-label img {
  display: block;
  height: 20px;
  margin-bottom: 2px;
}

.skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@screen lt-lg {
  .skeleton-container {
    flex-direction: row;
    align-items: center;
  }
}

.skeleton-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto;
  border-radius: 50%;
}

@screen lt-lg {
  .skeleton-icon {
    width: 70px;
    height: 70px;
    margin: 0;
  }
}

.skeleton-bar-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
}

.skeleton-bar-container :deep(.skelly) {
  border-radius: var(--radius-4);
}

.skeleton-bar-container :deep(.skelly:nth-child(1)) {
  width: 100%;
  height: 14px;
}

.skeleton-bar-container :deep(.skelly:nth-child(2)) {
  width: 100%;
  height: 10px;
  margin: 8px 0;
  border-radius: 999px;
}

.skeleton-bar-container :deep(.skelly:nth-child(3)) {
  width: 100%;
  height: 20px;
}
</style>
